import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { useStore } from '../../../../contexts/StoreContext';
import { Illustration } from '../../../illustration/Illustration';
import { ILLUSTRATION_NAMES } from '../../../illustration/illustration.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import BaseModal from '../../base-modal/BaseModal';
import { DynamicModalProps } from '../DynamicModal.types';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { useProductLogic } from 'src/hooks/use-product/useProduct.logic';
import { DYNAMIC_MODAL_GROUPS } from 'src/store/toast/toastStore.types';

export const ModalDeleteAll = observer(
  ({ type, dynamicModalVisible, dynamicModalLayer: { id, originSectionName } }: DynamicModalProps) => {
    const {
      project,
      i18n,
      toastStore,
      dynamicModal: { closeModal, activeModal },
    } = useStore();
    const { deleteProducts } = useProductLogic();

    const onPressConfirmButton = async () => {
      if (activeModal.productToDelete) {
        activeModal.onPressDeleteProduct();
        deleteProducts({
          deletedProducts: [activeModal.productToDelete],
        });
        amplitude.trackEvent(AnalyticsEvents.personalize.deleteProductConfirmClick, {
          n_products: project.totalNumberOfProducts,
          action: 'confirm',
          section_name: originSectionName,
        });
      } else if (activeModal.thumbnailsToDelete.length) {
        project.activeProject?.deleteImages(activeModal.thumbnailsToDelete);
        toastStore.showToast({
          message:
            activeModal.thumbnailsToDelete.length > 1
              ? i18n.t('toast:products-deleted-successfully')
              : i18n.t('toast:product-deleted-successfully'),
          duration: 3000,
          id: `delete-products-toast`,
          group: DYNAMIC_MODAL_GROUPS.Delete_Product,
        });
      } else {
        const isDeleteAll = project.activeGroup?.selectedProducts.length === project.activeGroup?.products.length;

        deleteProducts({
          deletedProducts: project.activeGroup?.selectedProducts,
        });

        const otherGroups = project.activeProject?.filteredGroups.filter(g => g.id !== project.activeGroup?.id);
        if (isDeleteAll && otherGroups && otherGroups.length > 0) {
          const firstFilledGroup = otherGroups[0];
          project.switchToGroup({ groupId: firstFilledGroup.id, scrollTo: 'top' });
        }
      }
      project.setFromSmartphoneOrFamilyAndFriends(false);
      closeModal();
    };

    const onReviewPressButton = async () => {
      closeModal();
    };

    return (
      <BaseModal
        testID={TestIDs.DELETE}
        modalIcon={
          <View style={{ width: 100, height: 100 }}>
            <Illustration name={ILLUSTRATION_NAMES.Delete} />
          </View>
        }
        title={i18n.t('modal-delete-all:title')}
        description={
          activeModal.productToDelete
            ? i18n.t('modal-delete-all:description')
            : i18n.t('modal-delete-all:description_n', {
                n_photo: project.activeGroup?.selectedProducts.length || activeModal.thumbnailsToDelete?.length,
              })
        }
        discardText={i18n.t('modal-delete-all:review')}
        confirmText={i18n.t('modal-delete-all:confirm')}
        handleDiscard={onReviewPressButton}
        handleConfirm={onPressConfirmButton}
        onClose={closeModal}
        onCloseOutside={closeModal}
        isVisible={dynamicModalVisible}
        type={type}
        id={id}
      />
    );
  },
);
